import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/system';
import EmptyMessage from 'core/components/EmptyMessage';
import { getOrderActionName } from 'core/utils/orderUtils';
import { useHolidays } from 'hooks/useHolidays';
import { isEmpty } from 'lodash';
import { useId, useState } from 'react';
import EventDetailDialog from '../EventDetailDialog';
import {
  checkToday,
  checkWeekend,
  createEventProps,
  getActionUserRoleName,
} from '../utils';
import DayTitle from './MonthDayList/DayTitle';
import { getEventsCount } from './utils';
import type { ViewComponentProps } from './types';
import type { EventExtendedProps } from '../CalendarPage';
import type { OrderActionsLog } from 'models/orderActionsLog';
import type { ReactElement } from 'react';

const MonthlyListViewMobile = ({
  groupedByMonthAndDay,
  handleDayClick,
}: ViewComponentProps): ReactElement => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<EventExtendedProps | null>(null);
  const componentId = useId();
  const dateFormatter = new Intl.DateTimeFormat('ru', {
    day: '2-digit',
    month: '2-digit',
  });

  const dateRange = {
    start: groupedByMonthAndDay[0]?.days[0]?.date ?? new Date(),
    end:
      groupedByMonthAndDay[groupedByMonthAndDay.length - 1]?.days?.[
        (groupedByMonthAndDay[groupedByMonthAndDay.length - 1]?.days?.length ?? 1) - 1
      ]?.date ?? new Date(),
  };

  const { holidays } = useHolidays(dateRange);

  if (groupedByMonthAndDay.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <EmptyMessage message='События не найдены' />
      </Box>
    );
  }

  const handleEventClick = (event: OrderActionsLog): void => {
    setSelectedEvent(createEventProps(event));
    setDialogOpen(true);
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const renderEventItem = (event: OrderActionsLog, dayDate: Date): ReactElement => (
    <ListItem
      key={`${componentId}-day-${dayDate.toISOString()}-event-${event.id}`}
      disablePadding>
      <ListItemButton
        onClick={(): void => {
          handleEventClick(event);
        }}>
        <ListItemText
          primary={`Заказ ${event.order.number}`}
          secondary={
            <>
              <Typography variant='body2' component='span'>
                {getOrderActionName(event.action)}, {event.order.client.fullName}
              </Typography>
              {!isEmpty(event.order.address) && (
                <Typography variant='body2' component='div' sx={{ mt: 0.5 }}>
                  {event.order.address}
                </Typography>
              )}
              {!isEmpty(event.user?.fullName) && (
                <Typography variant='body2' component='div' sx={{ mt: 0.5 }}>
                  {getActionUserRoleName(event.action)}:{' '}
                  {event.user?.fullName ?? 'Не задан'}
                </Typography>
              )}
            </>
          }
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <div>
      {groupedByMonthAndDay.map((monthGroup, monthIndex) => (
        <Box key={`${componentId}-${monthIndex}-${monthGroup.month.toISOString()}`}>
          <ListItem sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <ListItemText
              primary={monthGroup.month.toLocaleString('ru', {
                month: 'long',
                year: 'numeric',
              })}
            />
          </ListItem>
          <Divider />
          {monthGroup.days.map((day, dayIndex) => {
            const isWeekend = checkWeekend(day.date);
            const isToday = checkToday(day.date);
            const holidayInfo = holidays.find((h) => h.date.diff(day.date, 'day') === 0);

            return (
              <Accordion key={`${componentId}-calendar-day-${dayIndex}`}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  onClick={(): void => {
                    handleDayClick(day);
                  }}
                  sx={(theme) => ({
                    bgcolor: isToday
                      ? alpha(theme.palette.secondary.main, 0.08)
                      : undefined,
                  })}>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    width='100%'
                    spacing={1}>
                    <Box flex={1}>
                      <DayTitle
                        date={day.date}
                        isHoliday={holidayInfo?.isHoliday ?? false}
                        isShortDay={holidayInfo?.isShortDay ?? false}
                        isWeekend={isWeekend}
                        isToday={isToday}
                        holidayName={holidayInfo?.name}
                        dateFormatter={dateFormatter}
                        showTooltip={false}
                      />
                    </Box>
                    <Typography
                      color={
                        (holidayInfo?.isHoliday ?? false) && !isWeekend
                          ? 'warning.main'
                          : isWeekend
                          ? 'error.main'
                          : 'text.secondary'
                      }>
                      {getEventsCount(day)}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                  {isEmpty(day.events) ? (
                    <EmptyMessage message='События не найдены' />
                  ) : (
                    <List dense disablePadding>
                      {day.events?.map((event) => renderEventItem(event, day.date))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
          <Divider />
        </Box>
      ))}
      {selectedEvent && (
        <EventDetailDialog
          event={selectedEvent}
          open={dialogOpen}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
};

export default MonthlyListViewMobile;
